"use client";

import * as React from "react";
import { Check, ChevronsUpDown, X, ExternalLink } from "lucide-react";
import { cn } from "../../lib/utils";
import { Button } from "../../components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";
import { Badge } from "../../components/ui/badge";
import { useTenancy } from "@/contexts/TenancyContext";

// Helper to normalize framework keys for robust comparison (spaces, dashes, case, and version suffixes)
// E.g. iso27001, iso27001-2013, iso27001-2022 all normalize to 'iso27001'
//       nist80053, nist80053rev5, nist800-53-rev5 all normalize to 'nist80053'
//       pa, pacontrols, privacyact all normalize to 'privacyact'
const normalizeFrameworkKey = (str: string): string => {
  if (!str) return '';
  let key = str.toLowerCase().replace(/[^a-z0-9]/g, '');
  key = key.replace(/(iso\d{4,5})(2013|2022)?$/, '$1'); // iso27001-2013 -> iso27001
  key = key.replace(/(nist80053)(rev5)?$/, '$1'); // nist80053rev5 -> nist80053
  key = key.replace(/(nistcsf)(rev11)?$/, '$1'); // nistcsfrev11 -> nistcsf
  if (['pa', 'pacontrols', 'privacyact'].includes(key)) key = 'privacyact';
  if (['acsce8mm102021', 'e8', 'essential8'].includes(key)) key = 'essential8';
  if (['disp', 'dispcontrols'].includes(key)) key = 'disp';
  // if (['rffr', 'ism'].includes(key)) key = 'ism';
  return key;
};

// Helper function to convert standard names to pretty names
export const cleanStandardName = (standard: string): string => {
  if (!standard) return '';

  standard = standard.toLowerCase();
  const standardMap: Record<string, string> = {
    // Framework IDs from the provided list
    'pa': 'Privacy Act',
    'acsce8mm102021': 'Essential 8',
    'disp': 'DISP',
    'e8': 'Essential 8',
    'essential8': 'Essential 8',
    'iso45001': 'ISO 45001',
    'iso27001-2013': 'ISO 27001',
    'iso27001': 'ISO 27001',
    'soc2': 'SOC 2',
    'ism': 'ISM',
    'smb1001': 'SMB 1001',
    'iso27701': 'ISO 27701',
    'iso9001': 'ISO 9001',
    'iso42001': 'ISO 42001',
    'iso14001': 'ISO 14001',
    'rffr': 'RFFR',
    
    // Additional mappings from ControlsManager.util.ts
    // 'iso270012013annexacontrols': 'ISO 27001:2013',
    'acsc8mm112022idcontrols': 'Essential 8',
    'dispcontrols': 'DISP',
    'e8 controls': 'Essential 8',
    'e8controls': 'Essential 8',
    'soc2controls': 'SOC 2',
    'iso270012022annexacontrols': 'ISO 27001:2022',
    'iso270012022controls': 'ISO 27001:2022',
    'nist-800-171rev2': 'NIST 800-171',
    'nist-csf-rev1.1': 'NIST CSF',
    'nist800-53-rev5': 'NIST 800-53',
    'pacontrols': 'Privacy Act',
    'qgov-is18-controls': 'QGOV IS18',
    'nist80053rev5': 'NIST 800-53 Rev 5',
    'paControls': 'Privacy Act',
    'privacyact': 'Privacy Act',
    'nist80053': 'NIST 800-53',
    'nistcsf': 'NIST CSF',
  };

  // First try exact match, then try partial match for more flexible matching
  return standardMap[standard] || 
         standardMap[standard + 'controls'] || 
         standardMap[standard.replace(/[^a-z0-9]/g, '')] || 
         standard;
};

// Define the Framework type (standards are frameworks in this context)
export interface Framework {
  id: string;
  name: string;
  description?: string;
  standard?: string; // This is kept for backward compatibility but represents the same as name
}

// Props for the FrameworkCombobox component
export interface FrameworkComboboxProps {
  selected: string[];
  setSelected: (values: string[]) => void;
  placeholder?: string;
  className?: string;
  fullWidth?: boolean;
  disabled?: boolean; // Add support for disabling the component
  onFrameworkSelect?: (frameworkId: string) => void;
  onFrameworkDeselect?: (frameworkId: string) => void;
  onAllSelect?: () => void;
  onAllDeselect?: () => void;
  frameworks?: Framework[]; // Optionally pass frameworks directly
  fetchFrameworks?: () => Promise<Framework[]>; // Or provide a function to fetch them
}

// Create a context to provide frameworks data
interface FrameworksContextType {
  frameworks: Framework[];
  loading: boolean;
  error: Error | null;
  refresh: () => Promise<Framework[]>;
  standardFilters: string[];
  searchFrameworks: (query: string, standardFilter?: string) => Promise<void>;
}

const FrameworksContext = React.createContext<FrameworksContextType>({
  frameworks: [],
  loading: false,
  error: null,
  refresh: async () => [],
  standardFilters: [],
  searchFrameworks: async () => {},
});

// Provider component to fetch and manage frameworks data
export const FrameworksProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [frameworks, setFrameworks] = React.useState<Framework[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<Error | null>(null);
  const [standardFilters, setStandardFilters] = React.useState<string[]>([]);
  const { activeTenancy, enabledModules, isModuleEnabled } = useTenancy();
  const [initialized, setInitialized] = React.useState(false);
  
  // Enhanced console logging with styles
  const log = (message: string, data?: any, type: 'log' | 'warn' | 'error' | 'debug' = 'log') => {
    const styles = [
      'background: #1a365d',
      'color: white',
      'padding: 2px 8px',
      'border-radius: 4px',
      'font-weight: bold',
      'font-family: monospace',
      'font-size: 12px'
    ].join(';');
    
    const timestamp = new Date().toISOString().substring(11, 23);
    const logMessage = `%c[${timestamp}] FrameworksProvider: ${message}`;
    
    if (type === 'error') {
      console.error(logMessage, styles, data || '');
    } else if (type === 'warn') {
      console.warn(logMessage, styles, data || '');
    } else if (type === 'debug') {
      console.debug(logMessage, styles, data || '');
    } else {
      console.log(logMessage, styles, data || '');
    }
  };

  // Fetch frameworks using controlsService and enabled modules from TenancyContext
  const fetchFrameworks = React.useCallback(async () => {
    // Don't fetch if tenancy isn't ready
    if (!activeTenancy?.id || activeTenancy.id === 'Loading...') {
      log('Waiting for active tenancy...', { activeTenancy }, 'debug');
      setLoading(false);
      return [];
    }
    
    // Check if we have enabled modules
    if (!enabledModules || enabledModules.length === 0) {
      const errorMsg = 'No enabled modules found for this tenancy';
      log(errorMsg, { 
        tenancyId: activeTenancy.id,
        tenancyName: activeTenancy.name,
        enabledModules 
      }, 'warn');
      
      setFrameworks([]);
      setStandardFilters([]);
      setError(new Error(errorMsg));
      setLoading(false);
      return [];
    }

    try {
      setLoading(true);
      log('Fetching frameworks for tenancy', { 
        tenancyId: activeTenancy.id,
        tenancyName: activeTenancy.name 
      });
      
      // Import controlsService dynamically to avoid circular dependencies
      const controlsService = await import('@/services/controlsService');
      
      // Debug: Check if controlsService has the expected methods
      log('ControlsService methods', {
        getEnabledStandards: typeof controlsService.getEnabledStandards,
        getControlsByStandard: typeof controlsService.getControlsByStandard
      }, 'debug');
      
      // Use the enabled modules from TenancyContext as our frameworks/standards
      const enabledFrameworks = enabledModules;
      
      log('Processing enabled modules as frameworks/standards', { 
        enabledFrameworks,
        isArray: Array.isArray(enabledFrameworks),
        length: enabledFrameworks?.length 
      }, 'debug');
      
      if (!enabledFrameworks || !Array.isArray(enabledFrameworks) || enabledFrameworks.length === 0) {
        const errorMsg = 'No frameworks/standards found in enabled modules';
        log(errorMsg, { 
          tenancyId: activeTenancy.id,
          tenancyName: activeTenancy.name,
          enabledFrameworks
        }, 'warn');
        
        setFrameworks([]);
        setStandardFilters([]);
        setError(new Error(errorMsg));
        setLoading(false);
        return [];
      }
      
      // Map enabled modules to framework objects
      const uniqueFrameworks = enabledFrameworks.map(frameworkName => ({
        id: frameworkName.toLowerCase().replace(/\s+/g, '-'),
        name: frameworkName,
        description: `Framework/Standard: ${frameworkName}`,
        standard: frameworkName // For backward compatibility
      })) as Framework[];
      
      log('Extracted unique frameworks', { 
        frameworks: uniqueFrameworks,
        count: uniqueFrameworks.length 
      });
      
      setFrameworks(uniqueFrameworks);
      setError(null);
      setInitialized(true);
      
      log('Successfully initialized frameworks/standards', { 
        frameworkCount: uniqueFrameworks.length,
        standardCount: uniqueFrameworks.length 
      });
      
      return uniqueFrameworks;
    } catch (err) {
      const error = err as Error;
      log('Error fetching frameworks', {
        error: error.message,
        stack: error.stack,
        name: error.name
      }, 'error');
      
      setError(error);
      setFrameworks([]);
      throw error;
    } finally {
      setLoading(false);
      log('Fetch frameworks completed', { loading: false });
    }
  }, []);
  
  // Search frameworks with optional filtering
  const searchFrameworks = React.useCallback(async (query: string, standardFilter?: string) => {
    try {
      setLoading(true);
      
      // Get fresh frameworks from the fetchFrameworks function
      const results = await fetchFrameworks();
      let filtered = [...results];
      
      // Apply search query filter
      if (query) {
        const queryLower = query.toLowerCase();
        filtered = filtered.filter(framework => 
          framework.name.toLowerCase().includes(queryLower) || 
          (framework.description?.toLowerCase().includes(queryLower) ?? false) ||
          framework.id.toLowerCase().includes(queryLower)
        );
      }
      
      // Apply standard filter if provided
      if (standardFilter) {
        filtered = filtered.filter(framework => framework.standard === standardFilter);
      }
      
      // Update the filtered frameworks
      setFrameworks(filtered);
    } catch (err) {
      console.error('Error searching frameworks:', err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [fetchFrameworks]);
  
  // Initial load and reload when tenancy changes
  React.useEffect(() => {
    log('Tenancy changed', { 
      hasTenancy: !!activeTenancy?.id,
      tenancyId: activeTenancy?.id,
      tenancyName: activeTenancy?.name,
      isLoadingTenancy: activeTenancy?.id === 'Loading...'
    });

    if (activeTenancy?.id && activeTenancy.id !== 'Loading...') {
      log('Initializing FrameworksProvider', {
        tenancyId: activeTenancy.id,
        tenancyName: activeTenancy.name,
        enabledModulesCount: enabledModules?.length || 0
      });

      // Set initialized to true and fetch frameworks if we have enabled modules
      setInitialized(true);
      
      if (enabledModules && enabledModules.length > 0) {
        log('Tenancy ready, loading frameworks with enabled modules...', { 
          tenancyId: activeTenancy.id,
          enabledModules
        });
        fetchFrameworks().catch(error => {
          log('Failed to load frameworks', error, 'error');
        });
      } else {
        log('No enabled modules found, skipping framework fetch', { 
          tenancyId: activeTenancy.id,
          enabledModules 
        });
        setLoading(false);
      }
    } else if (!activeTenancy?.id) {
      log('No active tenancy, clearing frameworks');
      setFrameworks([]);
      setStandardFilters([]);
      setLoading(false);
    }
  }, [activeTenancy?.id, enabledModules, fetchFrameworks]);

  // Handle changes to enabledModules
  React.useEffect(() => {
    // Only run this effect if we have a valid tenancy and we're initialized
    if (!initialized || !activeTenancy?.id || activeTenancy.id === 'Loading...') {
      return;
    }

    if (enabledModules && enabledModules.length > 0) {
      log('Enabled modules changed, refreshing frameworks', {
        tenancyId: activeTenancy.id,
        enabledModulesCount: enabledModules.length,
        enabledModules
      });
      fetchFrameworks();
    } else {
      log('No enabled modules available, clearing frameworks', {
        tenancyId: activeTenancy.id
      });
      setFrameworks([]);
      setStandardFilters([]);
      setError(new Error('No enabled modules found for this tenancy'));
      setLoading(false);
    }
  }, [enabledModules, activeTenancy?.id, initialized, fetchFrameworks]);

  // Log when tenancy changes
  React.useEffect(() => {
    if (activeTenancy?.id) {
      log('Active tenancy changed', {
        id: activeTenancy.id,
        name: activeTenancy.name,
        plan: activeTenancy.plan
      });
      
      // Reset initialization state when tenancy changes
      setInitialized(false);
    }
  }, [activeTenancy?.id]);

  return (
    <FrameworksContext.Provider
      value={{
        frameworks,
        loading,
        error,
        refresh: fetchFrameworks,
        standardFilters,
        searchFrameworks,
      }}
    >
      {children}
    </FrameworksContext.Provider>
  );
};

// Hook to use the frameworks context
export const useFrameworks = () => {
  const context = React.useContext(FrameworksContext);
  if (context === undefined) {
    throw new Error('useFrameworks must be used within a FrameworksProvider');
  }
  return context;
};

// Main FrameworkCombobox component
export const FrameworkCombobox: React.FC<FrameworkComboboxProps> = ({
  selected,
  setSelected,
  placeholder = "Select frameworks...",
  className,
  fullWidth = false,
  disabled = false, // Default to enabled
  onFrameworkSelect,
  onFrameworkDeselect,
  onAllSelect,
  onAllDeselect,
  frameworks: propFrameworks,
  fetchFrameworks: propFetchFrameworks,
}) => {
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [activeFilter, setActiveFilter] = React.useState<string | null>(null);
  
  const { 
    frameworks: contextFrameworks, 
    loading, 
    error, 
    refresh, 
    standardFilters,
    searchFrameworks,
  } = useFrameworks();
  
  // Use frameworks from props if provided, otherwise use from context
  // DED-1026: Removed hardcoded RFFR injection - frameworks should only come from enabled modules
  const frameworks = propFrameworks || contextFrameworks;
 
  // Deduplicate and sort frameworks by display name (A-Z)
  const uniqueFrameworks = React.useMemo(() => {
    const seen = new Set<string>();
    const deduped = frameworks.filter(f => {
      const norm = normalizeFrameworkKey(f.id);
      if (seen.has(norm)) return false;
      seen.add(norm);
      return true;
    });
    return deduped.sort((a, b) => cleanStandardName(a.name).localeCompare(cleanStandardName(b.name)));
  }, [frameworks]);
  
  // Handle search query changes with debounce
  React.useEffect(() => {
    const debounceTimer = setTimeout(() => {
      searchFrameworks(searchQuery, activeFilter || undefined);
    }, 300);
    
    return () => clearTimeout(debounceTimer);
  }, [searchQuery, activeFilter, searchFrameworks]);
  
  // Load frameworks on initial mount
  React.useEffect(() => {
    searchFrameworks('');
  }, []);
    // Toggle framework selection
  const toggleFramework = (frameworkId: string) => {
    const canonicalId = frameworks.find(f => normalizeFrameworkKey(f.id) === normalizeFrameworkKey(frameworkId))?.id || frameworkId;
    const newSelected = [...selected];
    const index = newSelected.findIndex(sel => normalizeFrameworkKey(sel) === normalizeFrameworkKey(frameworkId));
    if (index === -1) {
      // Add to selection using canonical ID
      newSelected.push(canonicalId);
      setSelected(newSelected);
      onFrameworkSelect?.(canonicalId);
    } else {
      // Remove from selection
      newSelected.splice(index, 1);
      setSelected(newSelected);
      onFrameworkDeselect?.(canonicalId);
    }
  };
  // Check if a framework is selected (normalize both sides)
  const isSelected = (frameworkId: string) =>
    selected.some(sel => normalizeFrameworkKey(sel) === normalizeFrameworkKey(frameworkId));
  
  // Handle select all
  const handleSelectAll = () => {
    const allIds = frameworks.map(f => f.id);
    setSelected([...new Set([...selected, ...allIds])]);
    onAllSelect?.();
  };
  
  // Handle deselect all
  const handleDeselectAll = () => {
    setSelected([]);
    onAllDeselect?.();
  };
  
  // Get selected framework objects (normalize for robust matching), deduplicated by normalized id
  const selectedFrameworks = Array.from(
    new Map(
      frameworks
        .filter(f => selected.some(sel => normalizeFrameworkKey(sel) === normalizeFrameworkKey(f.id)))
        .map(f => [normalizeFrameworkKey(f.id), f])
    ).values()
  );

  console.log("Selected frameworks:", {selectedFrameworks, selected});
  
  return (
    <div className={cn("flex flex-col gap-2", fullWidth && "w-full", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <div className="relative">
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              disabled={disabled}
              className={cn(
                "w-full justify-between hover:bg-primary/2",
                "h-auto min-h-10",
                !selected.length && "text-muted-foreground",
                disabled ? "opacity-70 cursor-not-allowed" : "",
                className
              )}
            >
              <div className="flex flex-wrap gap-1 max-h-20 overflow-y-auto w-full">
                {selected.length > 0 ? (
                  selectedFrameworks.map(framework => (
                    <div key={framework.id} className="flex items-center gap-1 bg-primary/10 text-primary rounded-md px-2 py-1 text-sm">
                      <div className="flex items-center gap-2">
                        <span className="max-w-[200px] truncate text-sm">{
                          (() => {
                            const pretty = cleanStandardName(framework.name);
                            // If pretty name is just the id or a known alias, fallback to id->pretty
                            if (
                              pretty.toLowerCase() === framework.name.toLowerCase() ||
                              pretty.toLowerCase() === framework.id.toLowerCase()
                            ) {
                              return cleanStandardName(framework.id);
                            }
                            return pretty;
                          })()
                        }</span>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFramework(framework.id);
                        }}
                        className="opacity-50 hover:opacity-100 flex items-center justify-center h-4 w-4"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))
                ) : (
                  <span className="text-muted-foreground">{placeholder}</span>
                )}
              </div>
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
        </div>
        
        <PopoverContent className="w-[400px] p-0" align="start" forceMount>
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search frameworks..."
              value={searchQuery}
              onValueChange={setSearchQuery}
              className="border-0 focus:ring-0"
            />
            
            <CommandList 
              className="max-h-[400px] overflow-auto"
              onWheel={(e) => {
                // Prevent wheel events from propagating to parent elements
                e.stopPropagation();
              }}
            >
              {/* Standard Filter Buttons - Uncomment if needed */}
              {standardFilters.length > 0 && (
                <div className="flex flex-wrap gap-1 p-2 border-b overflow-auto max-h-[120px]">
                  <button
                    type="button"
                    onClick={() => setActiveFilter(null)}
                    className={cn(
                      "text-xs px-2 py-1 rounded-md whitespace-nowrap",
                      !activeFilter 
                        ? "bg-primary text-primary-foreground" 
                        : "bg-muted hover:bg-muted/80"
                    )}
                  >
                    All
                  </button>
                  {standardFilters.map(standard => (
                    <button
                      key={standard}
                      type="button"
                      onClick={() => setActiveFilter(standard)}
                      className={cn(
                        "text-xs px-2 py-1 rounded-md whitespace-nowrap",
                        activeFilter === standard
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted hover:bg-muted/80"
                      )}
                    >
                      {standard}
                    </button>
                  ))}
                </div>
              )}
              
              {/* Select/Deselect All */}
              <div className="flex justify-between items-center text-xs text-muted-foreground px-3 py-2 border-b">
                <span>{selected.length} selected</span>
                <div className="flex gap-2">
                  <button 
                    type="button" 
                    className="hover:underline"
                    onClick={handleSelectAll}
                  >
                    Select All
                  </button>
                  <span>|</span>
                  <button 
                    type="button" 
                    className="hover:underline"
                    onClick={handleDeselectAll}
                  >
                    Deselect All
                  </button>
                </div>
              </div>
              
              {loading ? (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  Loading frameworks...
                </div>
              ) : error ? (
                <div className="py-6 text-center text-sm text-destructive">
                  Error loading frameworks: {error.message}
                </div>
              ) : (
                <CommandGroup>
                  {uniqueFrameworks.map((framework) => {
                    const selectedItem = isSelected(framework.id);
                    return (
                      <CommandItem
                        key={framework.id}
                        value={framework.id}
                        onSelect={() => toggleFramework(framework.id)}
                        className={cn("cursor-pointer", selectedItem && "bg-accent/20 text-accent-foreground hover:bg-accent/20")}
                        data-selected={selectedItem}
                      >
                        <div className={cn(
                          "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                          selectedItem
                            ? "bg-primary/20 text-white"
                            : "opacity-50 [&_svg]:invisible"
                        )}>
                          <Check className={"h-4 w-4"} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{cleanStandardName(framework.name)}</span>
                            {framework.standard && framework.standard !== framework.name && (
                              <Badge variant="outline" className="text-xs">
                                {cleanStandardName(framework.standard)}
                              </Badge>
                            )}
                          </div>
                          {framework.description && (
                            <p className="text-xs text-muted-foreground line-clamp-1">
                              {framework.description.replaceAll('-2013', '-2022')}
                            </p>
                          )}
                        </div>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
    
    {/* Selected frameworks as badges */}
    {/* {selected.length > 0 && (
      <div className="flex flex-wrap gap-1">
        {selectedFrameworks.map(framework => (
          <Badge 
            key={framework.id} 
            variant="secondary"
            className="flex items-center gap-1"
          >
            {framework.name}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                toggleFramework(framework.id);
              }}
              className="ml-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 p-0.5"
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      
      {/* Selected frameworks as badges */}
      {/* {selected.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {selectedFrameworks.map(framework => (
            <Badge 
              key={framework.id} 
              variant="secondary"
              className="flex items-center gap-1"
            >
              {framework.name}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleFramework(framework.id);
                }}
                className="ml-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )} */}
    </div>
  );
};
